import React, { useEffect, useMemo, useState } from 'react';
import { CircleXIcon } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

import MobileAppBar from '@/components/molecules/MobileAppBar';
import CreateWorkActivityHeader from '@/components/molecules/workActivity/CreateWorkActivityHeader';
import CreateWorkActivityFooter from '@/components/molecules/workActivity/CreateWorkActivityFooter';
import CreateWorkActivitySteps from '@/components/organisms/workActivity/CreateWorkActivitySteps';
import CreateWorkActivityStepsMobile from '@/components/organisms/workActivity/CreateWorkActivityStepsMobile';
import CreateWorkActivityProfileCard from '@/components/organisms/workActivity/CreateWorkActivityProfileCard';
import CreateWorkActivityStep, {
  type WorkActivityStepFormValues
} from '@/components/organisms/workActivity/form-steps/CreateWorkActivityStep';
import CreateWorkAllocation from '@/components/organisms/workActivity/form-steps/CreateWorkAllocation';
import CreateWorkDocumentStep from '@/components/organisms/workActivity/form-steps/CreateWorkDocumentStep';
import InfoModal from '@/components/molecules/InfoModal';
import { workActivityStep1Schema } from '@/components/organisms/workActivity/form-steps/workActivityStep.schema';

import { accountingCenterService } from '@/services/accounting-centers.service';
import { bankAccountService } from '@/services/bank-accounts.service';
import { budgetService } from '@/services/budget.service';
import { workActivityService } from '@/services/work-activity.service';
import { AccountingCenter } from '@/services/types/accounting-centers-types';
import { BankAccount } from '@/services/types/bank-accounts.types';
import { getCurrentUser } from '@/store/slices/auth';
import {
  buildWorkActivityAllocations,
  buildWorkActivityBankAllocations,
  buildWorkActivityCreateFormData,
  getNormalizedActivityDuration,
  hasWorkActivityAllocations,
  hasWorkActivityBankAllocations,
  validateWorkActivityAllocations
} from '@/utils/workActivityForm';

import { PAGE } from '@/routes';

import { ReactComponent as WorkActivityIcon } from '@/assets/icons/work-activity.svg';

const HEADER_SUBTITLE: Record<number, string> = {
  1: 'Activity Details',
  2: 'Allocation',
  3: 'Document'
};

const WorkActivityCreatePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { budgetId: budgetIdParam } = useParams<{ budgetId: string }>();
  const budgetId = budgetIdParam || (location.state as { budgetId?: string } | undefined)?.budgetId;
  const currentUser = useSelector(getCurrentUser);

  const [activeStep, setActiveStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [accountingCenters, setAccountingCenters] = useState<AccountingCenter[]>([]);
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [uploadedDocuments, setUploadedDocuments] = useState<File[]>([]);
  const [currency, setCurrency] = useState('EUR');

  const defaultValues = useMemo(
    () => ({
      activity_name: '',
      description: '',
      activity_date: null,
      duration_hours: '',
      duration_minutes: '',
      bank_account: [],
      department_accounting_center: [],
      product_and_services_accounting_center: [],
      project_accounting_center: [],
      contract_accounting_center: []
    }),
    []
  );

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    trigger,
    clearErrors,
    getValues,
    control,
    formState: { errors }
  } = useForm<any>({
    resolver: zodResolver(workActivityStep1Schema),
    defaultValues,
    mode: 'onTouched'
  });

  const activityName = watch('activity_name');
  const durationHours = Number(watch('duration_hours')) || 0;
  const durationMinutes = Number(watch('duration_minutes')) || 0;

  const step1FieldNames = useMemo(
    () =>
      [
        'activity_name',
        'description',
        'activity_date',
        'duration_hours',
        'duration_minutes'
      ] as const satisfies Readonly<(keyof WorkActivityStepFormValues)[]>,
    []
  );

  useEffect(() => {
    if (!budgetId) return;

    accountingCenterService
      .getAccountingCenters(budgetId)
      .then(({ data }) => {
        setAccountingCenters(data);
      })
      .catch((error) => {
        console.error('Error fetching accounting centers:', error);
        setAccountingCenters([]);
      });

    bankAccountService
      .getBankAccounts(budgetId)
      .then(({ data }) => {
        setBankAccounts(data);
      })
      .catch((error) => {
        console.error('Error fetching bank accounts:', error);
        setBankAccounts([]);
      });

    budgetService
      .getBudget(budgetId)
      .then(({ data }) => {
        setCurrency(data.currency ?? 'EUR');
      })
      .catch((error) => {
        console.error('Error fetching budget:', error);
      });
  }, [budgetId]);

  const validateAllocationStep = (formValues: Record<string, unknown>) => {
    const result = validateWorkActivityAllocations(formValues);
    if (!result.isValid && result.errorMessage) {
      toast.error(result.errorMessage);
    }
    return result.isValid;
  };

  const showMissingEntitiesToast = (missingEntities: string[]) => {
    toast.custom(
      (t) => (
        <div
          className={`${
            t.visible ? 'animate-enter' : 'animate-leave'
          } max-w-md w-full bg-white shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5`}
        >
          <div className="flex-1 w-0 p-3">
            <div className="flex items-start">
              <div className="flex-shrink-0 pt-0.5">
                <CircleXIcon className="h-6 w-6 text-red-500" />
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm text-red-400 font-semibold text-gray-900">
                  {missingEntities.length === 1
                    ? 'Please create/enable at least one:'
                    : 'Please create/enable at least one of each:'}
                </p>
                <div className="mt-2 text-sm text-gray-700">
                  {missingEntities.map((entity, index) => (
                    <div key={index} className="flex items-start mt-1">
                      <span className="mr-2">•</span>
                      <span className="font-bold">{entity}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
      {
        duration: 5000
      }
    );
  };

  const validateRequiredEntities = () => {
    const missingEntities: string[] = [];

    if (bankAccounts.length === 0) {
      missingEntities.push('Bank Account');
    }

    const hasAccountingCenter =
      accountingCenters.filter((ac) => ac.type === 'DEPARTMENT_ACCOUNTING').length > 0 ||
      accountingCenters.filter((ac) => ac.type === 'CONTRACTS').length > 0 ||
      accountingCenters.filter((ac) => ac.type === 'PROJECTS').length > 0 ||
      accountingCenters.filter((ac) => ac.type === 'PRODUCTS_AND_SERVICES').length > 0;

    if (!hasAccountingCenter) {
      missingEntities.push('At least one Accounting Center needs to be enabled');
    }

    if (missingEntities.length > 0) {
      showMissingEntitiesToast(missingEntities);
      return false;
    }

    return true;
  };

  const onPrevClick = () => {
    if (activeStep <= 1) {
      if (budgetId) {
        navigate(`${PAGE.BUDGETS.path}/${budgetId}`);
      } else {
        navigate(-1);
      }
      return;
    }
    setActiveStep((s) => s - 1);
  };

  const onNextClick = async () => {
    if (activeStep === 1) {
      const ok = await trigger([...step1FieldNames]);
      if (!ok) {
        toast.error('Please correct the highlighted fields.');
        return;
      }

      if (!validateRequiredEntities()) {
        return;
      }

      setActiveStep(2);
      return;
    }

    if (activeStep === 2) {
      const formValues = getValues();
      if (validateAllocationStep(formValues)) {
        setActiveStep(3);
      }
      return;
    }

    handleSubmit(onFinish, () => {
      toast.error('Please correct the highlighted fields.');
    })();
  };

  const onFinish = async (data: WorkActivityStepFormValues) => {
    if (!budgetId) {
      toast.error('Budget ID is missing.');
      return;
    }

    const formValues = { ...getValues(), ...data };
    if (!validateAllocationStep(formValues)) {
      setActiveStep(2);
      return;
    }

    const allocations = buildWorkActivityAllocations(formValues);
    const bankAllocations = buildWorkActivityBankAllocations(formValues);

    if (!hasWorkActivityAllocations(allocations) && !hasWorkActivityBankAllocations(bankAllocations)) {
      toast.error('Please allocate this activity to at least one bank account or accounting center.');
      setActiveStep(2);
      return;
    }

    const activityDate = formValues.activity_date;
    if (!(activityDate instanceof Date)) {
      toast.error('Please select a valid activity date.');
      setActiveStep(1);
      return;
    }

    try {
      setIsSubmitting(true);

      const { hours: durationHours, minutes: durationMinutes } = getNormalizedActivityDuration(formValues);

      const formData = buildWorkActivityCreateFormData({
        activity: formValues.activity_name,
        description: formValues.description || '',
        activity_date: activityDate.toISOString(),
        duration_hours: durationHours,
        duration_minutes: durationMinutes,
        allocations,
        bank_allocations: bankAllocations,
        documents: uploadedDocuments
      });

      await workActivityService.createWorkActivity(budgetId, formData);
      setShowSuccessModal(true);
    } catch (error: any) {
      console.error('Error creating work activity:', error);
      toast.error(error?.message ?? 'Failed to log work activity. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoToBudgetOverview = () => {
    if (budgetId) {
      navigate(`${PAGE.BUDGETS.path}/${budgetId}`);
    }
  };

  return (
    <>
      <MobileAppBar
        title="Log Work Activity"
        onRightIconClick={() => {}}
        rightIcon={<WorkActivityIcon width={24} height={24} />}
      />

      <div className="flex-1 flex flex-col xl:flex-row items-stretch justify-between gap-6 w-full max-w-[1280px] mx-auto">
        <div className="hidden md:block xl:flex-1 xl:max-w-[224px] xl:sticky xl:top-0 space-y-6">
          <CreateWorkActivitySteps activeStep={activeStep} className="w-full flex-row xl:flex-col" />
          <CreateWorkActivityProfileCard
            activeStep={activeStep}
            userName={currentUser?.name}
            profilePicUrl={currentUser?.photoURL}
            hourlyRate={currentUser?.hourlyRate ?? 0}
            currency={currency}
            activityName={activityName}
            durationHours={durationHours}
            durationMinutes={durationMinutes}
          />
        </div>

        <div className="flex-1 flex flex-col gap-6 p-4 md:card !pb-8">
          <CreateWorkActivityHeader
            title="Log Your Activity"
            subtitle={HEADER_SUBTITLE[activeStep] ?? 'Activity Details'}
          />

          <div className="md:hidden">
            <CreateWorkActivityStepsMobile activeStep={activeStep} />
          </div>

          <div className="md:hidden">
            <CreateWorkActivityProfileCard
              activeStep={activeStep}
              userName={currentUser?.name}
              profilePicUrl={currentUser?.photoURL}
              hourlyRate={currentUser?.hourlyRate ?? 0}
              currency={currency}
              activityName={activityName}
              durationHours={durationHours}
              durationMinutes={durationMinutes}
            />
          </div>

          <form className="flex flex-col gap-6 flex-1" onSubmit={handleSubmit(onFinish)}>
            {activeStep === 1 && (
              <CreateWorkActivityStep
                register={register}
                errors={errors}
                watch={watch}
                setValue={setValue}
                clearErrors={clearErrors}
              />
            )}

            {activeStep === 2 && (
              <CreateWorkAllocation
                register={register}
                errors={errors}
                watch={watch}
                control={control}
                bankAccountsData={bankAccounts}
                accountingCentersData={accountingCenters}
                budgetId={budgetId}
                currency={currency}
              />
            )}

            {activeStep === 3 && (
              <CreateWorkDocumentStep files={uploadedDocuments} onFilesChange={setUploadedDocuments} />
            )}

            <CreateWorkActivityFooter
              activeStep={activeStep}
              onPrevClick={onPrevClick}
              onNextClick={onNextClick}
              loading={isSubmitting}
            />
          </form>
        </div>
      </div>

      <InfoModal
        isOpen={showSuccessModal}
        variant="success"
        title="Successfully Log Work Activity"
        message="Logged work activity will be reviewed by the supervisor"
        buttonText="Go to Budget Overview"
        onButtonClick={handleGoToBudgetOverview}
        icon=""
      />
    </>
  );
};

export default WorkActivityCreatePage;
